/**
 * filter-post.ts
 * Filtrage AJAX archive + pagination dynamique
 */

declare global {
    interface Window {
        ArchiveFilter?: {
            ajaxUrl: string;
            nonce: string;
        };
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface PostData {
    id: number;
    title: string;
    permalink: string;
    excerpt: string;
    date_raw: string;
    date: string;
    thumb: string;
    term: string;
}

interface AjaxResponse {
    success: boolean;
    data: {
        html: string;
        pagination: string;
        max_pages: number;
        found_posts: number;
        paged: number;
    };
}

interface PopStateData {
    term?: string;
    page?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Classe principale
// ─────────────────────────────────────────────────────────────────────────────

export default class ArchiveFilterClass {

    private readonly main: HTMLElement;
    private readonly POST_TYPE: string;
    private readonly TAXONOMY: string;
    private readonly PER_PAGE: number;
    private readonly filterBtns: NodeListOf<HTMLButtonElement>;
    private readonly resultsContainer: HTMLElement | null;
    private readonly filtersContainer: HTMLElement | null;

    private currentTerm = 'all';
    private currentPage = 1;
    private isLoading = false;

    constructor() {

        const main = document.getElementById('archive-main');

        if (!main) {
            console.warn('[ArchiveFilter] #archive-main introuvable');
            return;
        }

        // Vérifie que wp_localize_script fonctionne
        if (!window.ArchiveFilter) {
            console.error('[ArchiveFilter] window.ArchiveFilter introuvable');
            return;
        }


        this.main = main;

        this.POST_TYPE = main.dataset.postType ?? 'post';
        this.TAXONOMY  = main.dataset.taxonomy ?? 'category';
        this.PER_PAGE  = parseInt(main.dataset.perPage ?? '12', 10);

        this.filterBtns = main.querySelectorAll<HTMLButtonElement>(
            '.archive-filters__btn'
        );

        this.resultsContainer = main.querySelector<HTMLElement>(
            '.archive-listing-results'
        );

        this.filtersContainer = main.querySelector<HTMLElement>(
            '.archive-listing-header-filters-list'
        );

        this.bindFilterEvents();
        this.bindPopState();
        this.initFromUrl();
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Helpers
    // ─────────────────────────────────────────────────────────────────────────

    private setLoading(state: boolean): void {

        this.isLoading = state;

        this.resultsContainer?.classList.toggle(
            'is-loading',
            state
        );
    }

    private scrollToTop(): void {

        const header = this.main.querySelector(
            '.archive-listing-header'
        );

        const target = header ?? this.resultsContainer ?? this.main;

        const top =
            target.getBoundingClientRect().top +
            window.scrollY -
            80;

        window.scrollTo({
            top,
            behavior: 'smooth',
        });
    }

    private updateUrl(term: string, page: number): void {

        const url = new URL(window.location.href);

        url.pathname = url.pathname.replace(
            /\/page\/\d+\/?$/,
            '/'
        );

        if (term === 'all') {
            url.searchParams.delete('term');
        } else {
            url.searchParams.set('term', term);
        }

        if (page <= 1) {
            url.searchParams.delete('paged');
        } else {
            url.searchParams.set('paged', String(page));
        }

        history.pushState(
            { term, page } satisfies PopStateData,
            '',
            url.toString()
        );
    }

    private setActiveFilter(term: string): void {

        this.filterBtns.forEach(btn => {

            const active = btn.dataset.term === term;

            btn.classList.toggle('is-active', active);

            btn.setAttribute(
                'aria-pressed',
                String(active)
            );
        });
        this.updateFiltersVisualState(term);
    }

    private static esc(str: string): string {

        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    private renderPosts(html: string): void {

        if (!this.resultsContainer) return;

        this.resultsContainer.innerHTML = html;
    }


    private renderPagination(html: string): void {

        document.getElementById(
            'archive-pagination'
        )?.remove();

        if (!html.trim()) return;

        const nav = document.createElement('nav');

        nav.id = 'archive-pagination';

        nav.className = 'archive-pagination';

        nav.setAttribute(
            'aria-label',
            'Pagination'
        );

        nav.innerHTML = html;

        this.main.appendChild(nav);

        this.bindPaginationEvents(nav);
    }

    private bindPaginationEvents(nav: HTMLElement): void {

        nav.querySelectorAll<HTMLAnchorElement>(
            'a.page-numbers'
        ).forEach(link => {

            link.addEventListener('click', (e) => {

                e.preventDefault();

                const href = link.getAttribute('href');

                if (!href) return;

                const url = new URL(
                    href,
                    window.location.origin
                );

                // ── Récupération page ─────────────────────

                let page = 1;

                // Format ?paged=2
                const pagedParam =
                    url.searchParams.get('paged');

                if (pagedParam) {

                    page = parseInt(
                        pagedParam,
                        10
                    );

                } else {

                    // Format /page/2/
                    const match =
                        url.pathname.match(
                            /page\/(\d+)/
                        );

                    if (match?.[1]) {

                        page = parseInt(
                            match[1],
                            10
                        );
                    }
                }

                // ── Fetch AJAX ────────────────────────────

                this.currentPage = page;

                this.fetchPosts(
                    this.currentTerm,
                    this.currentPage
                );

                this.scrollToTop();
            });
        });
    }
    // ─────────────────────────────────────────────────────────────────────────
    // AJAX
    // ─────────────────────────────────────────────────────────────────────────

    private async fetchPosts(
        term: string,
        page: number
    ): Promise<void> {

        if (this.isLoading) return;

        if (!window.ArchiveFilter) {
            console.error(
                '[ArchiveFilter] Config introuvable'
            );
            return;
        }

        this.setLoading(true);

        const body = new FormData();

        body.append(
            'action',
            'archive_filter_posts'
        );

        body.append(
            'nonce',
            window.ArchiveFilter.nonce
        );

        body.append(
            'post_type',
            this.POST_TYPE
        );

        body.append(
            'taxonomy',
            this.TAXONOMY
        );

        body.append(
            'term',
            term
        );

        body.append(
            'paged',
            String(page)
        );

        body.append(
            'posts_per_page',
            String(this.PER_PAGE)
        );



        try {

            const response = await fetch(
                window.ArchiveFilter.ajaxUrl,
                {
                    method: 'POST',
                    body,
                }
            );

            const text = await response.text();

            const json = JSON.parse(text) as AjaxResponse;

            if (!json.success) {
                throw new Error(
                    'Erreur serveur AJAX'
                );
            }

            const {
                html,
                pagination,
                paged,
            } = json.data;

            this.renderPosts(html);

            this.renderPagination(pagination);

            this.updateUrl(term, page);

        } catch (err) {

            console.error(
                '[ArchiveFilter]',
                err
            );

        } finally {

            this.setLoading(false);
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Events
    // ─────────────────────────────────────────────────────────────────────────

    private bindFilterEvents(): void {

        this.filterBtns.forEach(btn => {

            btn.addEventListener('click', () => {

                const term =
                    btn.dataset.term ?? 'all';

                if (term === this.currentTerm) {
                    return;
                }

                this.setActiveFilter(term);

                this.currentTerm = term;

                this.currentPage = 1;

                this.fetchPosts(
                    this.currentTerm,
                    this.currentPage
                );

                this.scrollToTop();
            });
        });
    }

    private bindPopState(): void {

        window.addEventListener(
            'popstate',
            (e: PopStateEvent) => {

                const state =
                    e.state as PopStateData | null;

                if (!state) return;

                this.currentTerm =
                    state.term ?? 'all';

                this.currentPage =
                    state.page ?? 1;

                this.setActiveFilter(
                    this.currentTerm
                );

                this.fetchPosts(
                    this.currentTerm,
                    this.currentPage
                );
            }
        );
    }

    private initFromUrl(): void {

        const url = new URL(
            window.location.href
        );

        const term =
            url.searchParams.get('term');

        const page = parseInt(
            url.searchParams.get('paged') ?? '1',
            10
        );

        if (term && term !== 'all') {

            this.setActiveFilter(term);

            this.currentTerm = term;

            this.currentPage = page;

            this.fetchPosts(
                this.currentTerm,
                this.currentPage
            );

        } else if (page > 1) {

            this.currentPage = page;

            this.fetchPosts(
                this.currentTerm,
                this.currentPage
            );
        }
    }

    private updateFiltersVisualState(term: string): void {
        if (!this.filtersContainer) return;

        this.filtersContainer.classList.toggle(
            'has-specific-active',
            term !== 'all'
        );
    }
}